/**
 * VAT invoice evidence. A supplier VAT registration is a required particular
 * (reg 14). Charging VAT without one leaves input tax on HMRC discretion
 * (reg 29(2) / VIT31200). Do not infer a number.
 */

/** Terratech’s own VAT — never store it as the supplier’s. */
export const OWN_COMPANY_VAT_NUMBERS = ['GB483063587', '483063587'] as const;

export const VAT_EVIDENCE_STATES = [
  'evidenced',
  'defective',
  'not_applicable',
  'unread',
] as const;
export type VatEvidenceState = (typeof VAT_EVIDENCE_STATES)[number];

function compactVat(raw: string): string {
  return raw.replace(/[\s.-]/g, '').toUpperCase();
}

const OWN_VAT = new Set(
  OWN_COMPANY_VAT_NUMBERS.flatMap((n) => {
    const c = compactVat(n);
    return c.startsWith('GB') ? [c, c.slice(2)] : [c, `GB${c}`];
  }),
);

export function normalizeSupplierVatNumber(
  raw: string | null | undefined,
): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const printed = trimmed.length > 32 ? trimmed.slice(0, 32).trim() : trimmed;
  if (!printed) return null;
  const compact = compactVat(printed);
  if (OWN_VAT.has(compact)) return null;
  return printed;
}

/** True once extraction (or a later write) has looked at the issuer VAT field. */
export function vatNumberWasRead(suggested: unknown): boolean {
  return (
    suggested != null &&
    typeof suggested === 'object' &&
    !Array.isArray(suggested) &&
    'supplier_vat_number' in suggested
  );
}

export function vatEvidenceState(opts: {
  vat_pence: number | null;
  supplier_vat_number: string | null;
  vatNumberRead: boolean;
}): VatEvidenceState {
  if ((opts.vat_pence ?? 0) <= 0) return 'not_applicable';
  if (normalizeSupplierVatNumber(opts.supplier_vat_number)) return 'evidenced';
  if (!opts.vatNumberRead) return 'unread';
  return 'defective';
}
